import { useWorkTab } from '@/app/work-tabs/work-tab-context';
import { Link } from 'react-router-dom';
import { useCustomerCategoryLabel } from '@/features/settings';
import type { CustomFieldDefinition, PartnerListItem, PartnerType } from '@erp/shared';
import { formatCustomFieldValue, isPartnerSalesBlocked, LIST_VIEW_COLUMNS } from '@erp/shared';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type VisibilityState,
  type Updater,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Columns3 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { customFieldLabel } from '@/features/custom-fields';
import { PartnerRiskBadge } from '@/features/partners/components/partner-risk-badge';
import { PartnerRoleBadges } from '@/features/partners/components/partner-role-badges';
import { ProspectBulkDelete } from './prospect-bulk-delete';
import { formatDate, formatMoney } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  partnerNumber: 'partnerNumber',
  displayName: 'searchTerm',
  city: 'city',
  status: 'status',
  industry: 'industry',
  industryCode: 'industryCode',
  leadStatus: 'leadStatus',
  leadScore: 'leadScore',
  supplierRating: 'supplierRating',
  riskStatus: 'riskStatus',
};

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  ACTIVE: 'success',
  PROSPECT: 'secondary',
  BLOCKED: 'danger',
  INACTIVE: 'outline',
};

const SUPPLIER_RATING_VARIANTS: Record<string, BadgeVariant> = {
  A: 'success',
  B: 'warning',
  C: 'danger',
};

/** Spalten je Ansicht — Kunden, Interessenten und Lieferanten zeigen andere Kennzahlen. */
const COLUMNS_BY_TYPE: Record<PartnerType, readonly string[]> = {
  all: LIST_VIEW_COLUMNS.partners,
  customer: LIST_VIEW_COLUMNS.customers,
  prospect: LIST_VIEW_COLUMNS.prospects,
  supplier: LIST_VIEW_COLUMNS.suppliers,
};

function readVisibility(storageKey: string): VisibilityState {
  const raw = window.localStorage.getItem(storageKey);
  if (raw === null) {
    return { industryCode: false, priceGroup: false };
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return {
      industryCode: false,
      priceGroup: false,
      ...(typeof parsed === 'object' && parsed !== null ? (parsed as VisibilityState) : {}),
    };
  } catch {
    return { industryCode: false, priceGroup: false };
  }
}

export interface PartnerTableProps {
  canDelete?: boolean;
  savedColumns?: { id: string; visible: boolean }[];
  onSavedColumnsChange?: (columns: { id: string; visible: boolean }[]) => void;
  type: PartnerType;
  rows: PartnerListItem[];
  definitions: CustomFieldDefinition[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  sort: string;
  onSortChange: (sort: string) => void;
  onOpen: (id: string) => void;
  onCreate?: (() => void) | undefined;
  hasFilters: boolean;
}

export function PartnerTable({
  type,
  canDelete = false,
  savedColumns,
  onSavedColumnsChange,
  rows,
  definitions,
  isLoading,
  isError,
  onRetry,
  sort,
  onSortChange,
  onOpen,
  onCreate,
  hasFilters,
}: PartnerTableProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const workTab = useWorkTab();
  const categoryLabel = useCustomerCategoryLabel();
  const storageKey = `erp-partners-columns-${type}`;
  const [localVisibility, setLocalVisibility] = useState<VisibilityState>(() =>
    readVisibility(storageKey),
  );
  const columnVisibility = savedColumns
    ? Object.fromEntries(savedColumns.map((c) => [c.id, c.visible]))
    : localVisibility;
  const setColumnVisibility = (updater: Updater<VisibilityState>): void => {
    const next = typeof updater === 'function' ? updater(columnVisibility) : updater;
    if (savedColumns && onSavedColumnsChange)
      onSavedColumnsChange(savedColumns.map((c) => ({ ...c, visible: next[c.id] !== false })));
    else setLocalVisibility(next);
  };
  const [activeIndex, setActiveIndex] = useState(0);
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const selectable = type === 'prospect' && canDelete;
  const eligibleRows = rows.filter((row) => row.isProspect);
  const selected = eligibleRows.filter((row) => selectedIds.has(row.id));
  const allSelected = eligibleRows.length > 0 && selected.length === eligibleRows.length;

  useEffect(() => {
    setSelectedIds(
      (current) =>
        new Set(
          rows
            .filter((row) => selectable && row.isProspect && current.has(row.id))
            .map((row) => row.id),
        ),
    );
  }, [rows, selectable]);

  function selectionCheckbox(row?: PartnerListItem): JSX.Element {
    return (
      <Checkbox
        checked={
          row
            ? selectedIds.has(row.id)
            : allSelected
              ? true
              : selected.length > 0
                ? 'indeterminate'
                : false
        }
        disabled={deleting || isLoading || (row ? !row.isProspect : eligibleRows.length === 0)}
        aria-label={
          row
            ? t('partners.bulkDelete.select', { name: row.displayName })
            : t('partners.bulkDelete.selectPage')
        }
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        onCheckedChange={(checked) =>
          setSelectedIds((current) => {
            if (!row)
              return checked === true ? new Set(eligibleRows.map((entry) => entry.id)) : new Set();
            const next = new Set(current);
            if (checked === true) next.add(row.id);
            else next.delete(row.id);
            return next;
          })
        }
      />
    );
  }

  useEffect(() => {
    setLocalVisibility(readVisibility(storageKey));
  }, [storageKey]);

  useEffect(() => {
    window.localStorage.setItem(storageKey, JSON.stringify(localVisibility));
  }, [storageKey, localVisibility]);

  const listColumns = useMemo(
    () =>
      definitions.filter((definition) => definition.showInList && definition.archivedAt === null),
    [definitions],
  );

  const columns = useMemo<ColumnDef<PartnerListItem>[]>(() => {
    const all: Record<string, ColumnDef<PartnerListItem>> = {
      partnerNumber: {
        id: 'partnerNumber',
        accessorKey: 'partnerNumber',
        header: t('partners.columns.partnerNumber'),
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.partnerNumber}</span>,
      },
      displayName: {
        id: 'displayName',
        accessorKey: 'displayName',
        header: t('partners.columns.displayName'),
        cell: ({ row }) => (
          <Link
            to={`/partners/${row.original.id}`}
            onClick={(event) => event.stopPropagation()}
            className="font-medium underline-offset-4 hover:underline"
          >
            {row.original.displayName}
          </Link>
        ),
      },
      priceGroup: {
        id: 'priceGroup',
        header: t('priceGroups.field'),
        cell: ({ row }) =>
          row.original.priceGroupName
            ? `${row.original.priceGroup ?? ''} · ${row.original.priceGroupName}`
            : (row.original.priceGroup ?? '—'),
      },
      roles: {
        id: 'roles',
        header: t('partners.columns.roles'),
        cell: ({ row }) => (
          <PartnerRoleBadges
            isCustomer={row.original.isCustomer}
            isProspect={row.original.isProspect}
            isSupplier={row.original.isSupplier}
          />
        ),
      },
      status: {
        id: 'status',
        accessorKey: 'status',
        header: t('partners.columns.status'),
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANTS[row.original.status] ?? 'secondary'}>
            {t(`partners.statuses.${row.original.status}`)}
          </Badge>
        ),
      },
      riskStatus: {
        id: 'riskStatus',
        accessorKey: 'riskStatus',
        header: t('partners.columns.riskStatus'),
        cell: ({ row }) =>
          row.original.riskStatus === 'NORMAL' && !row.original.isBlocked ? (
            '—'
          ) : (
            <PartnerRiskBadge
              riskStatus={row.original.riskStatus}
              isBlocked={row.original.isBlocked}
            />
          ),
      },
      industry: {
        id: 'industry',
        accessorKey: 'industry',
        header: t('partners.columns.industry'),
        cell: ({ row }) => categoryLabel(row.original.industry),
      },
      industryCode: {
        id: 'industryCode',
        accessorKey: 'industryCode',
        header: t('partners.columns.industryCode'),
        cell: ({ row }) => row.original.industryCode ?? '—',
      },
      email: {
        id: 'email',
        accessorKey: 'email',
        header: t('partners.columns.email'),
        cell: ({ row }) => row.original.email ?? '—',
      },
      phone: {
        id: 'phone',
        accessorKey: 'phone',
        header: t('partners.columns.phone'),
        cell: ({ row }) => row.original.phone ?? '—',
      },
      city: {
        id: 'city',
        accessorKey: 'city',
        header: t('partners.columns.city'),
        cell: ({ row }) => {
          const value = `${row.original.postalCode ?? ''} ${row.original.city ?? ''}`.trim();
          return value === '' ? '—' : value;
        },
      },
      countryCode: {
        id: 'countryCode',
        accessorKey: 'countryCode',
        header: t('partners.columns.countryCode'),
      },
      tags: {
        id: 'tags',
        accessorKey: 'tags',
        header: t('partners.columns.tags'),
        cell: ({ row }) =>
          row.original.tags.length === 0 ? (
            '—'
          ) : (
            <span className="flex flex-wrap gap-1">
              {row.original.tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </span>
          ),
      },
      debtorAccountNumber: {
        id: 'debtorAccountNumber',
        accessorKey: 'debtorAccountNumber',
        header: t('partners.columns.debtorAccountNumber'),
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.debtorAccountNumber ?? '—'}</span>
        ),
      },
      paymentTermsDays: {
        id: 'paymentTermsDays',
        accessorKey: 'paymentTermsDays',
        header: t('partners.columns.paymentTermsDays'),
        cell: ({ row }) =>
          row.original.paymentTermsDays === null
            ? t('legacy.unknown')
            : t('partners.values.days', { count: row.original.paymentTermsDays }),
      },
      creditLimit: {
        id: 'creditLimit',
        accessorKey: 'creditLimit',
        header: t('partners.columns.creditLimit'),
        cell: ({ row }) => formatMoney(row.original.creditLimit, 'EUR', i18n.language),
      },
      leadStatus: {
        id: 'leadStatus',
        accessorKey: 'leadStatus',
        header: t('partners.columns.leadStatus'),
        cell: ({ row }) =>
          row.original.leadStatus === null ? (
            '—'
          ) : (
            <Badge variant="outline">{t(`partners.leadStatuses.${row.original.leadStatus}`)}</Badge>
          ),
      },
      leadSource: {
        id: 'leadSource',
        accessorKey: 'leadSource',
        header: t('partners.columns.leadSource'),
        cell: ({ row }) =>
          row.original.leadSource === null
            ? '—'
            : t(`partners.leadSources.${row.original.leadSource}`),
      },
      leadScore: {
        id: 'leadScore',
        accessorKey: 'leadScore',
        header: t('partners.columns.leadScore'),
        cell: ({ row }) => (row.original.leadScore === null ? '—' : String(row.original.leadScore)),
      },
      dealSizeEstimated: {
        id: 'dealSizeEstimated',
        accessorKey: 'dealSizeEstimated',
        header: t('partners.columns.dealSizeEstimated'),
        cell: ({ row }) => formatMoney(row.original.dealSizeEstimated, 'EUR', i18n.language),
      },
      winProbability: {
        id: 'winProbability',
        accessorKey: 'winProbability',
        header: t('partners.columns.winProbability'),
        cell: ({ row }) =>
          row.original.winProbability === null ? '—' : `${String(row.original.winProbability)} %`,
      },
      expectedClosingDate: {
        id: 'expectedClosingDate',
        accessorKey: 'expectedClosingDate',
        header: t('partners.columns.expectedClosingDate'),
        cell: ({ row }) => formatDate(row.original.expectedClosingDate, i18n.language),
      },
      creditorAccountNumber: {
        id: 'creditorAccountNumber',
        accessorKey: 'creditorAccountNumber',
        header: t('partners.columns.creditorAccountNumber'),
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.creditorAccountNumber ?? '—'}</span>
        ),
      },
      supplierRating: {
        id: 'supplierRating',
        accessorKey: 'supplierRating',
        header: t('partners.columns.supplierRating'),
        cell: ({ row }) =>
          row.original.supplierRating === null ? (
            '—'
          ) : (
            <Badge variant={SUPPLIER_RATING_VARIANTS[row.original.supplierRating] ?? 'secondary'}>
              {t(`partners.supplierRatings.${row.original.supplierRating}`)}
            </Badge>
          ),
      },
      minimumOrderValue: {
        id: 'minimumOrderValue',
        accessorKey: 'minimumOrderValue',
        header: t('partners.columns.minimumOrderValue'),
        cell: ({ row }) => formatMoney(row.original.minimumOrderValue, 'EUR', i18n.language),
      },
      deliveryTimeDays: {
        id: 'deliveryTimeDays',
        accessorKey: 'deliveryTimeDays',
        header: t('partners.columns.deliveryTimeDays'),
        cell: ({ row }) =>
          row.original.deliveryTimeDays === null
            ? '—'
            : t('partners.values.days', { count: row.original.deliveryTimeDays }),
      },
    };

    const base = COLUMNS_BY_TYPE[type]
      .map((id) => all[id])
      .filter((column): column is ColumnDef<PartnerListItem> => column !== undefined);

    const dynamic: ColumnDef<PartnerListItem>[] = listColumns.map((definition) => ({
      id: `cf-${definition.id}`,
      header: customFieldLabel(definition, i18n.language),
      cell: ({ row }) =>
        formatCustomFieldValue(
          definition,
          row.original.customFields[definition.key],
          i18n.language,
        ) || '—',
    }));

    return [...base, ...dynamic];
  }, [categoryLabel, t, i18n.language, listColumns, type]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnVisibility, columnOrder: savedColumns?.map((c) => c.id) ?? [] },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  useEffect(() => {
    setActiveIndex(0);
  }, [rows]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!workTab.active) return;
      const target = event.target as HTMLElement | null;
      const isInteractive = target?.closest(
        'input, textarea, select, button, a, summary, [contenteditable="true"], [role="combobox"], [role="menu"], [role="listbox"], [role="dialog"], [role="alertdialog"]',
      );
      if (
        event.defaultPrevented ||
        isInteractive ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        rows.length === 0
      ) {
        return;
      }

      if (event.key === 'j' || event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, rows.length - 1));
        return;
      }
      if (event.key === 'k' || event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
        return;
      }
      if (event.key === 'Enter') {
        const row = rows[activeIndex];
        if (row !== undefined) {
          event.preventDefault();
          onOpen(row.id);
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [rows, activeIndex, onOpen, workTab.active]);

  useEffect(() => {
    const active = bodyRef.current?.querySelector<HTMLTableRowElement>('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const [sortField, sortOrder] = sort.split(':');

  const toggleSort = (columnId: string): void => {
    const field = SORT_FIELD_BY_COLUMN[columnId];
    if (field === undefined) {
      return;
    }
    const nextOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    onSortChange(`${field}:${nextOrder}`);
  };

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-md border border-border p-6">
        <p className="text-sm text-muted-foreground">{t('partners.states.error')}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {selectable ? (
        <ProspectBulkDelete
          selected={selected}
          onBusyChange={setDeleting}
          onDeleted={(ids) =>
            setSelectedIds((current) => new Set([...current].filter((id) => !ids.includes(id))))
          }
        />
      ) : null}
      <div className="flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Columns3 className="h-4 w-4" aria-hidden="true" />
              {t('partners.columnsMenu')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
            <DropdownMenuLabel>{t('partners.columnsMenu')}</DropdownMenuLabel>
            {table.getAllLeafColumns().map((column) => (
              <DropdownMenuCheckboxItem
                key={column.id}
                checked={column.getIsVisible()}
                onCheckedChange={(checked) => column.toggleVisibility(checked === true)}
              >
                {typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Ab `md` Tabelle, darunter Kartenliste — siehe Responsive-Regeln. */}
      <div className="hidden rounded-md border border-border md:block">
        {isLoading ? (
          <div className="flex flex-col gap-2 p-4" aria-busy="true">
            {Array.from({ length: 8 }, (_, index) => (
              <Skeleton key={index} className="h-9 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 p-10 text-center">
            <p className="text-sm text-muted-foreground">
              {hasFilters ? t('partners.states.emptyFiltered') : t('partners.states.empty')}
            </p>
            {onCreate ? (
              <Button size="sm" onClick={onCreate}>
                {t('partners.actions.create')}
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="max-h-[65vh] overflow-auto">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {selectable ? (
                      <TableHead className="w-12">{selectionCheckbox()}</TableHead>
                    ) : null}
                    {headerGroup.headers.map((header) => {
                      const field = SORT_FIELD_BY_COLUMN[header.column.id];
                      const isSorted = field !== undefined && field === sortField;
                      return (
                        <TableHead
                          key={header.id}
                          aria-sort={
                            isSorted ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'
                          }
                        >
                          {field === undefined ? (
                            flexRender(header.column.columnDef.header, header.getContext())
                          ) : (
                            <button
                              type="button"
                              className="flex items-center gap-1 hover:text-foreground"
                              onClick={() => toggleSort(header.column.id)}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {isSorted ? (
                                sortOrder === 'asc' ? (
                                  <ArrowUp className="h-3 w-3" aria-hidden="true" />
                                ) : (
                                  <ArrowDown className="h-3 w-3" aria-hidden="true" />
                                )
                              ) : (
                                <ChevronsUpDown className="h-3 w-3 opacity-50" aria-hidden="true" />
                              )}
                            </button>
                          )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody ref={bodyRef}>
                {table.getRowModel().rows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    data-active={index === activeIndex}
                    tabIndex={0}
                    className={cn(
                      'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      index === activeIndex && 'bg-accent/60',
                      // Gesperrte Partner müssen schon in der Liste ins Auge fallen.
                      isPartnerSalesBlocked(row.original) &&
                        'border-destructive/30 bg-destructive/10 hover:bg-destructive/20',
                    )}
                    onClick={() => onOpen(row.original.id)}
                    onFocus={() => setActiveIndex(index)}
                    onKeyDown={(event) => {
                      if (
                        (event.key === 'Enter' || event.key === ' ') &&
                        event.target === event.currentTarget
                      ) {
                        event.preventDefault();
                        onOpen(row.original.id);
                      }
                    }}
                  >
                    {selectable ? (
                      <TableCell className="w-12">{selectionCheckbox(row.original)}</TableCell>
                    ) : null}
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 md:hidden" data-testid="partner-mobile-list">
        {selectable && !isLoading && eligibleRows.length > 0 ? (
          <div className="flex min-h-11 items-center gap-3 text-sm">
            {selectionCheckbox()}
            <span>{t('partners.bulkDelete.selectPage')}</span>
          </div>
        ) : null}
        {isLoading ? (
          Array.from({ length: 5 }, (_, index) => <Skeleton key={index} className="h-24 w-full" />)
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-md border border-border p-10 text-center">
            <p className="text-sm text-muted-foreground">
              {hasFilters ? t('partners.states.emptyFiltered') : t('partners.states.empty')}
            </p>
            {onCreate ? (
              <Button size="sm" onClick={onCreate}>
                {t('partners.actions.create')}
              </Button>
            ) : null}
          </div>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="flex items-center gap-3">
              {selectable ? selectionCheckbox(row) : null}
              <button
                type="button"
                className={cn(
                  'flex min-h-11 min-w-0 flex-1 flex-col items-start gap-1 rounded-md border border-border p-3 text-left hover:bg-accent',
                  isPartnerSalesBlocked(row) && 'border-destructive/30 bg-destructive/10',
                )}
                onClick={() => onOpen(row.id)}
              >
                <span className="flex w-full items-center justify-between gap-2">
                  <span className="font-medium">{row.displayName}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {row.partnerNumber}
                  </span>
                </span>
                <PartnerRiskBadge riskStatus={row.riskStatus} isBlocked={row.isBlocked} />
                <PartnerRoleBadges
                  isCustomer={row.isCustomer}
                  isProspect={row.isProspect}
                  isSupplier={row.isSupplier}
                />
                <span className="text-xs text-muted-foreground">
                  {[row.email, `${row.postalCode ?? ''} ${row.city ?? ''}`.trim()]
                    .filter((entry) => entry !== null && entry !== '')
                    .join(' · ') || '—'}
                </span>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
